import { type PropsWithChildren, useState } from 'react';

import * as Sentry from '@sentry/browser';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import cn from '@/lib/utils/cn';

import { Button, type Props as ButtonProps } from '../shadcn/button';

type Props = PropsWithChildren<
  { isSubmitting?: boolean; onSubmit?: () => Promise<unknown> } & Omit<
    ButtonProps,
    'onSubmit'
  >
>;

export default function SubmitButton({
  onSubmit,
  children,
  isSubmitting: isSubmittingProp,
  ...props
}: Props) {
  const { t } = useTranslation();

  const [isSubmittingLocal, setIsSubmitting] = useState(false);

  const isSubmitting = isSubmittingLocal || isSubmittingProp;

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      await onSubmit?.();
    } catch (error) {
      Sentry.captureException(error);
    }

    setIsSubmitting(false);
  };

  return (
    <Button
      onClick={onSubmit ? handleSubmit : undefined}
      type={onSubmit ? 'button' : 'submit'}
      {...props}
      className={cn('relative', props.className)}
    >
      <span
        className={cn(
          'inline-flex items-center justify-center gap-2 whitespace-nowrap',
          { ['opacity-0']: isSubmitting },
        )}
      >
        {children ?? t('SAVE')}
      </span>

      <span className="absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center">
        <Loader2
          className={cn('animate-spin', {
            ['hidden']: !isSubmitting,
          })}
        />
      </span>
    </Button>
  );
}
